/**
 * Migration script for cookie consent preferences
 * 
 * This script helps migrate from the old cookie-alert system to the new
 * unified consent banner that handles both Matomo and Meta Pixel.
 * 
 * COMPORTEMENT:
 * - Le pixel FB est activé par défaut si le visiteur ne choisit rien
 * - La bannière s'affiche uniquement si meta-pixel-consent est vide
 * - Pas d'expiration automatique du consentement
 */

export function migrateCookieConsent() {
    const oldKey = "cookie-alert";
    const oldValue = localStorage.getItem(oldKey);
    
    // Check if old system was used and new system is not yet configured
    const hasMatomoConsent = localStorage.getItem("matomo-consent") !== null;
    const hasMetaPixelConsent = localStorage.getItem("meta-pixel-consent") !== null;
    
    // Only migrate if old system was used but new system is not
    if (oldValue === "true" && !hasMatomoConsent && !hasMetaPixelConsent) {
        console.log("[Cookie Migration] Migrating old cookie preferences...");
        
        // Old system only tracked if user closed the banner
        // We assume they accepted if they dismissed it
        localStorage.setItem("matomo-consent", "accepted");
        localStorage.setItem("meta-pixel-consent", "accepted"); // Migrer vers accepté par défaut
        
        console.log("[Cookie Migration] Migration complete");
        console.log("[Cookie Migration] Matomo: accepted");
        console.log("[Cookie Migration] Meta Pixel: accepted (default behavior)");
        
        return true;
    }
    
    return false;
}

/**
 * Reset all cookie consent preferences
 * Useful for testing or allowing users to change their mind
 */
export function resetCookieConsent() {
    localStorage.removeItem("cookie-alert");
    localStorage.removeItem("matomo-consent");
    localStorage.removeItem("meta-pixel-consent");
    console.log("[Cookie Consent] All preferences have been reset");
    console.log("[Cookie Consent] Reload page to see banner and pixel FB will be active by default");
}

/**
 * Get current consent status
 * Returns an object with the current state of all consents
 */
export function getConsentStatus() {
    const matomoConsent = localStorage.getItem("matomo-consent");
    const metaPixelConsent = localStorage.getItem("meta-pixel-consent");
    
    return {
        matomo: matomoConsent,
        metaPixel: metaPixelConsent,
        metaPixelEffective: metaPixelConsent === "refused" ? "refused" : "accepted (default or explicit)",
        hasResponded: metaPixelConsent !== null,
        bannerWillShow: metaPixelConsent === null
    };
}

/**
 * Check if user has given consent for a specific service
 * @param {string} service - "matomo" or "meta-pixel"
 * @returns {boolean}
 */
export function hasConsent(service) {
    const key = service === "matomo" ? "matomo-consent" : "meta-pixel-consent";
    return localStorage.getItem(key) === "accepted";
}

/**
 * Test if Meta Pixel would be loaded
 * Simule le comportement du pixel FB
 */
export function testMetaPixelBehavior() {
    const metaPixelConsent = localStorage.getItem("meta-pixel-consent");
    
    if (metaPixelConsent === null) {
        console.log("[Meta Pixel Test] No consent stored → Pixel WILL load (default behavior)");
        return "will-load-default";
    } else if (metaPixelConsent === "accepted") {
        console.log("[Meta Pixel Test] Consent = accepted → Pixel WILL load");
        return "will-load-accepted";
    } else if (metaPixelConsent === "refused") {
        console.log("[Meta Pixel Test] Consent = refused → Pixel WILL NOT load");
        return "will-not-load";
    }
}

// Auto-run migration on load
if (typeof window !== "undefined") {
    migrateCookieConsent();
}

// Export for window access (useful for debugging in console)
if (typeof window !== "undefined") {
    window.CookieConsentTools = {
        migrate: migrateCookieConsent,
        reset: resetCookieConsent,
        status: getConsentStatus,
        hasConsent: hasConsent,
        testPixel: testMetaPixelBehavior
    };
    
    console.log("[Cookie Consent] Debug tools available:");
    console.log("  window.CookieConsentTools.status() - Check current consent status");
    console.log("  window.CookieConsentTools.reset() - Reset all consents (banner will show on reload)");
    console.log("  window.CookieConsentTools.testPixel() - Test if Meta Pixel would load");
    console.log("  window.CookieConsentTools.hasConsent('meta-pixel') - Check specific consent");
}